import React from "react";
import { ExternalLink, Star, Store, Loader2, PackageSearch } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { demoCopy } from "../i18n/demoCopy";

// Renders real product search results with graceful states. NEVER shows invented data.
export default function ProductResults({ loading, result, onChoose }) {
  const { t, lang } = useI18n();

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 text-center" data-testid="products-loading">
        <Loader2 className="h-8 w-8 animate-spin text-xmas-gold" />
        <p className="text-sm text-xmas-champagne">{t("prod.searching")}</p>
      </div>
    );
  }
  if (!result) return null;

  if (result.state === "not_configured") {
    return (
      <div className="rounded-xl border border-xmas-gold/30 bg-xmas-gold/10 p-4 text-sm text-xmas-champagne" data-testid="products-not-configured">
        {t("prod.not_configured")}
      </div>
    );
  }
  if (result.state === "error") {
    return (
      <div className="rounded-xl border border-xmas-crimson/40 bg-xmas-crimson/10 p-4 text-sm text-xmas-crimson" data-testid="products-error">
        {t("prod.error")}
      </div>
    );
  }
  const products = result.products || [];
  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-xmas-slate" data-testid="products-empty">
        <PackageSearch className="h-7 w-7 text-xmas-slate" />
        {result?.demo_unprepared ? <span className="text-xmas-champagne" data-testid="products-demo-unprepared">{demoCopy(lang).landing.searchDisabled}</span> : t("prod.none")}
      </div>
    );
  }

  return (
    <div data-testid="products-results">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {products.map((p, i) => (
          <div key={`${p.product_link || p.title}-${i}`} className="flex gap-3 rounded-xl border border-xmas-gold/15 bg-xmas-navy/40 p-3" data-testid={`product-card-${i}`}>
            {p.thumbnail ? (
              <img src={p.thumbnail} alt="" className="h-20 w-20 shrink-0 rounded-lg bg-white object-contain" />
            ) : (
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg bg-xmas-navysurface"><Store className="h-6 w-6 text-xmas-slate" /></div>
            )}
            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 text-sm font-medium text-xmas-snow">{p.title}</p>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                {p.price && <span className="font-mono-num text-base text-xmas-gold">{p.price}</span>}
                {p.old_price && <span className="font-mono-num text-xs text-xmas-slate line-through">{p.old_price}</span>}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-xmas-slate">
                {p.source && <span className="inline-flex items-center gap-1"><Store className="h-3 w-3" />{p.source}</span>}
                {p.rating != null && <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 fill-xmas-gold text-xmas-gold" />{p.rating}</span>}
                {p.delivery && <span className="truncate">{p.delivery}</span>}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {onChoose && (
                  <button onClick={() => onChoose(p)} data-testid={`product-choose-${i}`}
                    className="btn-gold inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider">
                    {t("prod.choose")}
                  </button>
                )}
                {p.product_link && (
                  <a href={p.product_link} target="_blank" rel="noreferrer" data-testid={`product-link-${i}`}
                    className="inline-flex items-center gap-1 rounded-full bg-xmas-gold/20 px-3 py-1 text-xs font-semibold text-xmas-gold transition hover:bg-xmas-gold/30">
                    {t("prod.view")} <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] italic text-xmas-slate">{t("prod.disclaimer")}</p>
    </div>
  );
}
